import { useCallback, useMemo, useRef, useState } from "react";
import { usePopup } from "../../../hooks/usePopup";
import { useHttpClient } from "../../../hooks/useHttpClient";
import { useLocalization } from "../../../stores/localizationContext";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import { useUserContext } from "../../../stores/userContext";
import { Dropdown } from "../../Common/Dropdown/Dropdown";
import { ConfirmPopup } from "../../Popups/ConfirmPopup";
import type { SessionChatMessage } from "../../../interfaces/sessionChatMessage";
import "./Chat.scss";

interface ChatProps {
  messages?: SessionChatMessage[];
  isNewestMsgVisible?: boolean;
  readMsgsCount?: number;
  onSendMessage?: (message: string) => void;
  currentUserId?: string;
}

export const Chat = ({
  messages,
  isNewestMsgVisible = true,
  readMsgsCount = 0,
  onSendMessage,
  currentUserId,
}: ChatProps) => {
  const { t } = useLocalization();
  const { user } = useUserContext();
  const api = useHttpClient();
  const joinedStudents = useLiveSessionStore((state) => state.joinedStudents);
  const removeStudent = useLiveSessionStore((state) => state.removeStudent);
  const sendMessageToEngine = useLiveSessionStore((state) => state.sendMessageToEngine);
  const sessionData = useLiveSessionStore((state) => state.sessionData);

  const newestMsgRef = useRef<HTMLLIElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openMessageDropdownId, setOpenMessageDropdownId] =
    useState<string>("");
  const actionBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  
  // State for kickout popup
  const [showKickoutPopup, setShowKickoutPopup] = useState(false);
  const [studentToKickout, setStudentToKickout] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Mock data for development
  const defaultMessages: SessionChatMessage[] = [
    {
      id: "1",
      username: "Julian Grayson",
      message: "I don't understand the first part of the question.",
      action: "message",
      userRole: "student",
    },
    {
      id: "1a",
      username: "Julian Grayson",
      message: "I don't understand the first part of the question.",
      action: "message",
      userRole: "student",
    },
    {
      id: "3",
      username: "Jameson Peyton",
      message: "I mean the top part",
      action: "message",
      userRole: "student",
    },
    {
      id: "2",
      username: "System",
      message: "Emmett Weston joined the session.",
      action: "join_session",
    },
    {
      id: "3a",
      username: "System",
      message: "Dany Cooper has been removed.",
      action: "kick_out_session",
    },
    {
      id: "3b",
      username: "System",
      message: "Julian Grayson left the session.",
      action: "leave_session",
    },
    {
      id: "4",
      username: "Julian Grayson",
      message: "I don't understand the first part of the question.",
      action: "message",
      userRole: "student",
    },
  ];

  const chatMessages =
    messages !== undefined && messages !== null ? messages : defaultMessages;

  // Track locally deleted messages so UI updates immediately
  const [locallyDeletedMessageIds, setLocallyDeletedMessageIds] = useState<string[]>([]);

  // Memoized filtered messages for performance
  const filteredMessages = useMemo(
    () =>
      chatMessages.filter(
        (msg) => !msg.isDeleted && !locallyDeletedMessageIds.includes(msg.id)
      ),
    [chatMessages, locallyDeletedMessageIds]
  );

  const unreadCount = Math.max((chatMessages.length || 0) - readMsgsCount, 0);

  // Scroll to newest message
  const scrollToNewest = useCallback(() => {
    newestMsgRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Handle message send
  const handleSendMessage = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && inputRef.current?.value.trim()) {
        const message = inputRef.current.value.trim();
        onSendMessage?.(message);
        inputRef.current.value = "";
      }
    },
    [onSendMessage]
  );

  // Get CSS class for message type
  const getMessageClassName = useCallback((action?: string) => {
    switch (action) {
      case "join_session":
        return "notifications notifications--green";
      case "leave_session":
      case "kick_out_session":
        return "notifications notifications--red";
      default:
        return "";
    }
  }, []);
  const [showDeleteMessagePopup, setShowDeleteMessagePopup] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);

  // Find student by username
  const findStudentByUsername = useCallback((username: string) => {
    return joinedStudents.find(
      (student) => 
        student.firstName === username || 
        `${student.firstName} ${student.lastName}`.trim() === username ||
        `${student.firstName || ""}` === username
    );
  }, [joinedStudents]);

  // Handle kick out student
  const handleKickOutStudent = useCallback(async () => {
    if (!studentToKickout) return;

    try {
      let endpoint = import.meta.env.VITE_KICK_OUT_STUDENT;
      if (endpoint.includes("{session_id}"))
        endpoint = endpoint.replace(
          "{session_id}",
          sessionData?.sessionId || ""
        );
      if (endpoint.includes("{educator_id}"))
        endpoint = endpoint.replace("{educator_id}", user?.userId || "");
      if (endpoint.includes("{student_id}"))
        endpoint = endpoint.replace("{student_id}", studentToKickout.id);

      const response = await api.post(endpoint);

      if (response.status === 200) {
        removeStudent(studentToKickout.id);
        sendMessageToEngine("addMutualSessionActionLog", {
          resource: "session",
          action: "kick_out",
          receiver: {
            id: studentToKickout.id,
            name: studentToKickout.name,
            type: "student",
          },
        });

        setShowKickoutPopup(false);
        setStudentToKickout(null);
        setOpenMessageDropdownId("");
      }
    } catch (err) {
      console.error("Failed to kick out student:", err);
      // Keep popup open on error
    }
  }, [
    studentToKickout,
    sessionData?.sessionId,
    user?.userId,
    api,
    removeStudent,
    sendMessageToEngine,
  ]);

  // Open kickout popup
  const openKickoutPopup = useCallback((username: string) => {
    const student = findStudentByUsername(username);
    if (student) {
      setStudentToKickout({ 
        id: student.userId, 
        name: student.firstName || username 
      });
      setShowKickoutPopup(true);
    }
  }, [findStudentByUsername]);

  // Handle delete message
  const handleDeleteMessage = useCallback(() => {
    if (!messageToDelete) return;

    // Send delete message action to engine
    sendMessageToEngine("deleteMessage", {
      messageId: messageToDelete,
    });

    // Optimistically remove from UI
    setLocallyDeletedMessageIds((prev) =>
      prev.includes(messageToDelete) ? prev : [...prev, messageToDelete]
    );

    // Close popup and reset state
    setShowDeleteMessagePopup(false);
    setMessageToDelete(null);
    setOpenMessageDropdownId("");
  }, [messageToDelete, sendMessageToEngine]);

  const deleteMessagePopupContent = useMemo(() => {
    if (!showDeleteMessagePopup) return null;

    return (
      <ConfirmPopup
        onConfirm={handleDeleteMessage}
        onCancel={() => {
          setShowDeleteMessagePopup(false);
          setMessageToDelete(null);
        }}
        icon=""
        title={t("sidebar_delete_message_label")}
        message={t("want_to_delete")}
        confirmText={t("delete_button")}
        cancelText={t("cancel_label")}
        extraClass=""
      />
    );
  }, [showDeleteMessagePopup, handleDeleteMessage, t]);
  const DeleteMessagePopupComponent = usePopup(deleteMessagePopupContent);

  // Kickout popup content
  const kickOutPopupContent = useMemo(() => {
    if (!showKickoutPopup) return null;

    return (
      <ConfirmPopup
        onConfirm={handleKickOutStudent}
        onCancel={() => {
          setShowKickoutPopup(false);
          setStudentToKickout(null);
        }}
        icon="user-times-icon"
        title={t("remove_student_title")}
        message={t("kick_out_student_message")}
        confirmText={t("remove_button")}
        cancelText={t("cancel_label")}
        extraClass="kick-out-popup"
      />
    );
  }, [showKickoutPopup, handleKickOutStudent, t]);
  const KickOutPopupComponent = usePopup(kickOutPopupContent);

  return (
    <div className="chat">
      <ul>
        {filteredMessages.map((payload, index) => {
          const isNotification = [
            "join_session",
            "leave_session",
            "kick_out_session",
          ].includes(payload.action || "");
          const prev = filteredMessages[index - 1];
          const isSameSender =
            !isNotification &&
            prev &&
            !["join_session", "leave_session", "kick_out_session"].includes(
              prev.action || ""
            ) &&
            prev.username === payload.username;
          const isSender = payload.id === currentUserId;
          const liClassNames = [
            isNotification ? getMessageClassName(payload.action) : "",
            isSameSender ? "same-sender" : "",
            isSender ? "sender" : "",
          ]
            .filter(Boolean)
            .join(" ")
            .trim();

          // Use combination of id and index to ensure unique keys
          const uniqueKey = `${payload.id}-${index}`;

          if (isNotification) {
            const iconClass =
              payload.action === "join_session"
                ? "sign-in-alt-icon"
                : payload.action === "leave_session"
                ? "sign-out-alt-icon"
                : "user-times-icon";
            return (
              <li key={uniqueKey} className={liClassNames}>
                <i className={`f-icon ${iconClass} fa-medium`}> </i>
                <h5>{payload.message}</h5>
              </li>
            );
          }

          return (
            <li
              key={uniqueKey}
              ref={
                index === filteredMessages.length - 1 ? newestMsgRef : undefined
              }
              className={liClassNames}
            >
              <div className="message">
                <h3 dir="auto">
                  {payload.username}
                  <span>: </span>
                </h3>
                <p dir="auto">{payload.message}</p>
              </div>
              <div className="action">
                <div className="dropdown">
                  <button
                    ref={(el) => {
                      actionBtnRefs.current[uniqueKey] = el;
                    }}
                    className="btn btn-link-dark dropdown-toggle"
                    onClick={() =>
                      setOpenMessageDropdownId((prev) =>
                        prev === uniqueKey ? "" : uniqueKey
                      )
                    }
                  >
                    <i className="f-icon ellipsis-vertical-icon"> </i>
                  </button>
                  <Dropdown
                    open={openMessageDropdownId === uniqueKey}
                    anchorRef={{
                      current: actionBtnRefs.current[uniqueKey],
                    }}
                    onClose={() => setOpenMessageDropdownId("")}
                    className="dropdown-menu dropdown-menu--message"
                    offsetY={1}
                  >
                    <ul className="dropdown-menu__list dropdown-menu__list--message">
                      <li>
                        <button
                          className="btn btn-link-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMessageToDelete(payload.id);
                            setShowDeleteMessagePopup(true);
                            setOpenMessageDropdownId("");
                          }}
                        >
                          Delete Message
                          <div className="action-button">
                            <i className="f-icon trash-alt-icon fa-light"> </i>
                          </div>
                        </button>
                      </li>
                      {payload.userRole === "student" && findStudentByUsername(payload.username) && (
                        <li>
                          <button
                            className="btn btn-link-dark"
                            onClick={(e) => {
                              e.stopPropagation();
                              openKickoutPopup(payload.username);
                              setOpenMessageDropdownId("");
                            }}
                          >
                            Remove Student
                            <div className="action-button">
                              <i className="f-icon user-times-icon fa-light"> </i>
                            </div>
                          </button>
                        </li>
                      )}
                    </ul>
                  </Dropdown>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {!isNewestMsgVisible && unreadCount > 0 && (
        <div className="messages-notification" onClick={scrollToNewest}>
          <div className="scroll-arrow">
            <a href="#">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M8 12l-4-4h8l-4 4z" />
              </svg>
            </a>
          </div>
          <div className="notification">
            <span>{unreadCount}</span>
          </div>
        </div>
      )}

      {onSendMessage && (
        <div className="chat-input">
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a message..."
            onKeyPress={handleSendMessage}
            dir="auto"
          />
        </div>
      )}
      <DeleteMessagePopupComponent />
      <KickOutPopupComponent />
    </div>
  );
};
